import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Loader from '../../../components/Common/Loader';
import ShowValidationError from '../../../components/Common/ValidationError';
import { getUSStates } from '../../../config/usa-states';
import { getCountries, postalCodeFormatMap } from '../../../config/countries';
import { UserModel, UserShippingAddress } from '../../../interfaces/user.interface';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { ProfileShippingAddressValues } from '../../Profile/Profile';
import { updateUserPhipToAddress } from '../../Profile/services/Profile.service';
import { GooglePlacesService } from '../../../services/googlePlaces.service';
import { zipCodeMaskToRegex } from '../../../helper/helper';
import BackButton from '../../../components/Common/BackButton';

interface ShippingAddressFormValues {
  shippingName: string;
  recipientName: string;
  shippingAddress1: string;
  shippingAddress2: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  shippingCountry: string;
}

export const enhancedValidationSchema = Yup.object().shape({
  shippingEmail: Yup.string().email('Invalid email address').required('Email is required'),
  customerName: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  recipientName: Yup.string().when(['readonlyShippingAddress'], {
    is: (readonlyShippingAddress: boolean) => readonlyShippingAddress,
    then: (schema) =>
      schema
        .required("Recipient's name is required")
        .min(2, 'Name must be at least 2 characters')
        .max(50, 'Name must be less than 50 characters')
        .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
    otherwise: (schema) => schema.optional(),
  }),
  shippingAddress1: Yup.string()
    .required('Address is required')
    .min(5, 'Address must be at least 5 characters')
    .max(250, 'Address must be less than 250 characters')
    .test('is-valid-address', 'Please enter a valid address', async function (value) {
      if (!value || value.length < 5) return true;
      const { shippingCountry, shippingAddress2, shippingCity, shippingState, shippingZip } = this.parent;
      const countryCode = postalCodeFormatMap[shippingCountry || 'United States']?.code || 'US';
      try {
        const fullAddress = [value, shippingAddress2, shippingCity, shippingState, shippingZip]
          .filter(Boolean)
          .map((item) => item?.toString().trim())
          .filter(Boolean)
          .join(', ');
        const isValid = await GooglePlacesService.validateAddress(fullAddress, countryCode);
        return isValid;
      } catch {
        return this.createError({ message: 'Error validating address' });
      }
    }),
  shippingAddress2: Yup.string()
    .max(250, 'Address must be less than 250 characters')
    .matches(/^[a-zA-Z0-9\s,.\-#]*$/, 'Address contains invalid characters'),
  shippingState: Yup.string()
    .required('State is required')
    .min(2, 'State must be at least 2 characters')
    .max(50, 'State must be less than 50 characters'),
  shippingCity: Yup.string()
    .required('City is required')
    .min(2, 'City must be at least 2 characters')
    .max(50, 'City must be less than 50 characters')
    .matches(/^[a-zA-Z\s'-]+$/, 'City can only contain letters, spaces, hyphens, and apostrophes'),
  shippingCountry: Yup.string().required('Country is required'),
  shippingZip: Yup.string()
    .required('Zip code is required')
    .test('country-zip-mask', async function (value) {
      const { shippingCountry } = this.parent;
      const format = postalCodeFormatMap[shippingCountry];
      if (!value) return true;
      if (format.mask) {
        const regex = zipCodeMaskToRegex(format.mask);
        if (!regex.test(value)) {
          return this.createError({ message: `Zip code must match format: ${format.example}` });
        }
      }
      const countryCode = format.code || 'US';
      try {
        const isValid = await GooglePlacesService.validatePostalCode(value, countryCode);
        if (!isValid) {
          return this.createError({ message: `Invalid postal code for ${shippingCountry}` });
        }
        return isValid;
      } catch {
        return this.createError({ message: 'Error validating postal code' });
      }
    }),
});

const shippingAddressSchema = Yup.object()
  .shape({
    shippingName: Yup.string().required('Company name is required'),
    recipientName: Yup.string().required("Recipient's name is required"),
    shippingAddress1: Yup.string()
      .required('Address is required')
      .min(5, 'Address must be at least 5 characters')
      .max(250, 'Address must be less than 250 characters'),
    shippingAddress2: Yup.string().matches(/^[a-zA-Z0-9\s,.\-#]*$/, 'Address contains invalid characters'),
    shippingCity: Yup.string()
      .required('City is required')
      .min(2, 'City must be at least 2 characters')
      .max(50, 'City must be less than 50 characters')
      .matches(/^[a-zA-Z\s'-]+$/, 'City can only contain letters, spaces, hyphens, and apostrophes'),
    shippingState: Yup.string()
      .required('State is required')
      .min(2, 'State must be at least 2 characters')
      .max(50, 'State must be less than 50 characters'),
    shippingZip: Yup.string()
      .required('Zip code is required')
      .test('country-zip-mask', async function (value) {
        const { shippingCountry } = this.parent;
        const format = postalCodeFormatMap[shippingCountry];
        if (!value) return true;
        if (format.mask) {
          const regex = zipCodeMaskToRegex(format.mask);
          if (!regex.test(value)) {
            return this.createError({ message: `Zip code must match format: ${format.example}` });
          }
        }
        const countryCode = format.code || 'US';
        try {
          const isValid = await GooglePlacesService.validatePostalCode(value, countryCode);
          if (!isValid) {
            return this.createError({ message: `Invalid postal code for ${shippingCountry}` });
          }
          return isValid;
        } catch {
          return this.createError({ message: 'Error validating postal code' });
        }
      }),
    shippingCountry: Yup.string().required('Country is required'),
  })
  .test('is-valid-full-address', 'Please enter a valid address', async function (obj) {
    const { shippingAddress1, shippingAddress2, shippingCountry, shippingCity, shippingZip, shippingState } = obj;

    if (!shippingAddress1 || shippingAddress1.length < 5) return true;

    const countryCode = postalCodeFormatMap[shippingCountry || 'United States']?.code || 'US';
    const fullAddress = [
      shippingAddress1?.trim(),
      shippingAddress2?.trim(),
      shippingCity?.trim(),
      shippingState?.trim(),
      shippingZip?.trim(),
    ]
      .filter(Boolean)
      .join(', ');

    const isValid = await GooglePlacesService.validateAddress(fullAddress, countryCode);

    return isValid
      ? true
      : this.createError({
          path: 'shippingAddress1',
          message: 'Address is not valid',
        });
  });

interface ProfileFormProps {
  user: UserModel | null;
  onSaved: () => void;
  showEditPreOrderButton?: boolean;
  handleEditPreOrders?: () => void;
  hideTitle?: boolean;
  submitButtonTitle?: string;
  controlsClass?: string;
  formClass?: string;
  handleBack?: () => void;
  useValidation?: boolean;
}

// Create a context to track form submission attempts
const FormSubmitContext = createContext(false);

const ShippingAddressForm = () => {
  const {
    errors: shippingErrors,
    handleChange: shippingHandleChange,
    values: shippingValues,
  } = useFormikContext<ShippingAddressFormValues>();
  const [usaStates, setUsaStates] = useState<Array<string>>([]);
  const [countries, setCountries] = useState<Array<string>>([]);
  const formSubmitAttempted = useContext(FormSubmitContext);

  useEffect(() => {
    console.log(shippingErrors);
  }, [shippingErrors]);

  useEffect(() => {
    if (!usaStates?.length) {
      setUsaStates(getUSStates());
      setCountries(getCountries());
    }
  }, []);

  return (
    <div className="flex flex-col gap-3 sm:gap-2 w-full">
      <div className="w-full">
        <input
          type="text"
          name="shippingName"
          value={shippingValues.shippingName}
          onChange={shippingHandleChange}
          placeholder="School name"
          className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
        />
        <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingName || '' : ''} />
      </div>

      <div className="w-full">
        <input
          type="text"
          name="recipientName"
          value={shippingValues.recipientName}
          onChange={shippingHandleChange}
          placeholder="Recipient's Name"
          className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
        />
        <ShowValidationError error={formSubmitAttempted ? shippingErrors.recipientName || '' : ''} />
      </div>

      <div className="w-full">
        <input
          type="text"
          name="shippingAddress1"
          value={shippingValues.shippingAddress1}
          onChange={shippingHandleChange}
          placeholder="Address 1"
          className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
        />
        <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingAddress1 || '' : ''} />
      </div>
      <div className="w-full">
        <input
          type="text"
          name="shippingAddress2"
          value={shippingValues.shippingAddress2}
          onChange={shippingHandleChange}
          placeholder="Address 2 (Optional)"
          className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
        />
        <ShowValidationError
          error={formSubmitAttempted ? (shippingValues.shippingAddress2 && shippingErrors.shippingAddress1) || '' : ''}
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-7 mt-1">
        <div className="w-full">
          <select
            name="shippingCountry"
            value={shippingValues.shippingCountry}
            onChange={shippingHandleChange}
            className="p-[0.5rem] sm:p-[0.6rem] w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
          >
            {countries.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingCountry || '' : ''} />
        </div>
        <div className="w-full">
          <input
            type="text"
            name="shippingCity"
            value={shippingValues.shippingCity}
            onChange={shippingHandleChange}
            placeholder="City"
            className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
          />
          <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingCity || '' : ''} />
        </div>
      </div>
      <div className="w-full flex items-start gap-7 mt-1">
        <div className="w-full">
          {shippingValues.shippingCountry === 'United States' ? (
            <select
              name="shippingState"
              value={shippingValues.shippingState}
              onChange={shippingHandleChange}
              className="p-[0.5rem] sm:p-[0.6rem] w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
            >
              <option value="" disabled selected hidden>
                State
              </option>
              {usaStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              name="shippingState"
              value={shippingValues.shippingState}
              onChange={shippingHandleChange}
              placeholder={shippingValues.shippingCountry === 'United Kingdom' ? 'County' : 'State/Region'}
              className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText"
            />
          )}
          <ShowValidationError
            error={
              formSubmitAttempted
                ? shippingErrors.shippingState
                  ? shippingValues.shippingCountry === 'United Kingdom'
                    ? shippingErrors.shippingState?.replace('State', 'County')
                    : shippingValues.shippingCountry !== 'United States'
                      ? shippingErrors.shippingState?.replace('State', 'State/Region')
                      : shippingErrors.shippingState
                  : ''
                : ''
            }
          />
        </div>
        <div className="w-full">
          <input
            type="text"
            name="shippingZip"
            value={shippingValues.shippingZip}
            onChange={shippingHandleChange}
            placeholder="Zip"
            className="py-2 px-3 w-full rounded-lg border-secondaryStoryLightBlueText border border-opacity-20 text-secondaryStoryBlueText font-bold focus:outline-none focus:border-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
          />
          <ShowValidationError error={formSubmitAttempted ? shippingErrors.shippingZip || '' : ''} />
        </div>
      </div>
    </div>
  );
};

const ShipToAddressForm: React.FC<ProfileFormProps> = ({
  user,
  onSaved,
  showEditPreOrderButton,
  handleEditPreOrders,
  hideTitle,
  submitButtonTitle = 'Continue',
  controlsClass = '',
  formClass = '',
  handleBack,
  useValidation = false,
}: ProfileFormProps) => {
  const formikRef = useRef<any>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const requiredFormFields = useMemo(
    () => [
      'shippingName',
      'recipientName',
      'shippingAddress1',
      'shippingCity',
      'shippingState',
      'shippingZip',
      'shippingCountry',
    ],
    [],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (formikRef.current && user) {
        const formikIsValid = formikRef.current.isValid;
        const hasAllRequiredFields = requiredFormFields.every(
          (field) => formikRef.current.values[field] && formikRef.current.values[field].trim() !== '',
        );
        
        // Get the original shipping address from backend
        const shippingAddress: UserShippingAddress = user.shippingAddresses?.length
          ? user.shippingAddresses[0]
          : ({} as UserShippingAddress);
        
        // Check if form has changes by comparing with backend values
        const currentValues = formikRef.current.values;
        const formHasChanges = Object.keys(currentValues).some((key) => {
          const currentValue = currentValues[key] || '';
          const originalValue = shippingAddress[key as keyof UserShippingAddress] || '';
          return currentValue !== originalValue;
        });
        
        setIsValid(formikIsValid && hasAllRequiredFields);
        setHasChanges(formHasChanges);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [requiredFormFields, user]);

  const [values, setValues] = useState<{ shippingAddress: ProfileShippingAddressValues }>({
    shippingAddress: {
      shippingName: '',
      recipientName: '',
      shippingAddress1: '',
      shippingAddress2: '',
      shippingState: '',
      shippingCountry: 'United States',
      shippingCity: '',
      shippingZip: '',
    },
  });

  useEffect(() => {
    if (user) {
      const shippingAddress: UserShippingAddress = user.shippingAddresses?.length
        ? user.shippingAddresses[0]
        : ({} as UserShippingAddress);
      setValues({
        ...values,
        shippingAddress: {
          ...values.shippingAddress,
          ...shippingAddress,
          shippingName: shippingAddress?.shippingName || '',
          recipientName:
            shippingAddress?.recipientName || (user && `${user.firstName || ''} ${user.lastName || ''}`.trim()) || '',
        },
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name in values.shippingAddress) {
      setValues((prevValues) => ({
        ...prevValues,
        shippingAddress: {
          ...prevValues.shippingAddress,
          [name]: value,
        },
      }));
    } else {
      setValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement> | any) => {
    if (e.preventDefault) {
      e.preventDefault();
    }

    // If we got a form data object from the ProfileForm component
    if (e.shippingAddress) {
      // This is coming from our custom form submission with shipping address
      setValues((prevValues) => ({
        ...prevValues,
        ...e,
        shippingAddress: {
          ...prevValues.shippingAddress,
          ...e.shippingAddress,
        },
      }));
    }

    // Make sure we use the updated values
    const dataToSubmit = e.shippingAddress ? e : values;
    setLoader(true);
    try {
      await updateUserPhipToAddress(dataToSubmit, () => {
        onSaved();
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  // Helper function to create a synthetic event with preventDefault
  const createSyntheticEvent = (formData: any): React.FormEvent<HTMLFormElement> => {
    const syntheticEvent = {
      ...formData,
      preventDefault: () => {},
      target: {
        elements: {
          // Add any elements that might be accessed in handleSubmit
        },
      },
    } as unknown as React.FormEvent<HTMLFormElement>;
    return syntheticEvent;
  };

  const handleShippingAddressSubmit = (shippingValues: ShippingAddressFormValues) => {
    // Create a synthetic event with the combined values
    const formData = {
      ...values,
      shippingAddress: shippingValues,
    };

    // Call the parent handleSubmit with a synthetic event
    const syntheticEvent = createSyntheticEvent(formData);
    handleSubmit(syntheticEvent);
  };

  const getInitialShippingValues = (): ShippingAddressFormValues => {
    if (values.shippingAddress) {
      return {
        shippingName: values.shippingAddress.shippingName || '',
        recipientName:
          values.shippingAddress.recipientName || (user && `${user?.firstName} ${user?.lastName}`.trim()) || '',
        shippingAddress1: values.shippingAddress.shippingAddress1 || '',
        shippingAddress2: values.shippingAddress.shippingAddress2 || '',
        shippingCity: values.shippingAddress.shippingCity || '',
        shippingState: values.shippingAddress.shippingState || '',
        shippingZip: values.shippingAddress.shippingZip || '',
        shippingCountry: values.shippingAddress.shippingCountry || 'United States',
      };
    }
    return {
      shippingName: '',
      recipientName: `${user?.firstName} ${user?.lastName}`.trim(),
      shippingAddress1: '',
      shippingAddress2: '',
      shippingCity: '',
      shippingState: '',
      shippingZip: '',
      shippingCountry: 'United States',
    };
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Set that form submission was attempted
    setFormSubmitAttempted(true);

    // If we have a shipping address form and user has enabled saving shipping address
    if (formikRef.current && user?.saveDefaultShippingAddress) {
      // Validate the Formik form
      const errors = await formikRef.current.validateForm();

      // Mark all fields as touched to show validation errors
      const touchedFields: Record<string, boolean> = {};
      Object.keys(getInitialShippingValues()).forEach((key) => {
        touchedFields[key] = true;
      });
      formikRef.current.setTouched(touchedFields);

      // If there are validation errors, don't submit
      if (Object.keys(errors).length > 0) {
        console.log('Validation errors:', errors);
        return;
      }

      // Get the current values from Formik
      const shippingValues = formikRef.current.values;

      const missingFields = requiredFormFields.filter((field) => !shippingValues[field]);

      if (missingFields.length > 0) {
        console.log('Missing required fields:', missingFields);
        return;
      }

      // Create the combined form data
      const formData = {
        ...values,
        shippingAddress: shippingValues,
      };

      // Call the parent handleSubmit with a synthetic event
      const syntheticEvent = createSyntheticEvent(formData);
      handleSubmit(syntheticEvent);
    } else {
      // If no shipping form, just submit the main form with the original event
      handleSubmit(e);
    }
  };

  return (
    <div className="max-w-7xl w-full mx-auto font-sofiaSans">
      {!hideTitle && (
        <h1 className="flex justify-center text-lg font-bold mb-4">What is your school's ship-to address?</h1>
      )}
      <form
        id="ship-to-form"
        name="ship-to-form"
        className={`text-start ${formClass || ''}`}
        onSubmit={handleFormSubmit}
      >
        <FormSubmitContext.Provider value={formSubmitAttempted}>
          <Formik
            initialValues={getInitialShippingValues()}
            validationSchema={shippingAddressSchema}
            onSubmit={handleShippingAddressSubmit}
            enableReinitialize={true}
            innerRef={formikRef}
          >
            <ShippingAddressForm />
          </Formik>
        </FormSubmitContext.Provider>
        <div className={`flex flex-wrap justify-end gap-2 md:flex-nowrap mt-5 ${controlsClass || ''}`}>
          {handleBack && <BackButton onClick={handleBack}></BackButton>}
          {showEditPreOrderButton && (
            <button
              onClick={handleEditPreOrders}
              className="bg-transparent text-storyblueButton py-2 px-6 rounded-full transition-all"
            >
              Edit Pre-Orders
            </button>
          )}
          <button
            type="submit"
            className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center ${
              !useValidation || (isValid && hasChanges) ? '' : 'opacity-50 cursor-not-allowed'
            }`}
            disabled={loader || (useValidation && (!isValid || !hasChanges))}
          >
            {submitButtonTitle}
            <Loader loader={loader} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ShipToAddressForm;
